import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type {
  TabContent,
  TabContentAction,
} from '../CustomDash/components/PageConfigProvider/page-reducer';
import type { GlobalAssistantEntityType } from './global-ai-assistant-api';
import { pickLiveChartPreviewMessageIds } from './global-ai-assistant-chart-preview-policy';
import {
  type GlobalAiChartPreviewRuntimeSnapshot,
  type GlobalAiPipelineTimelineEvent,
  useGlobalAiChartPreviewRuntimeStore,
} from './global-ai-assistant-chart-preview-runtime';
import type { GlobalAiConversationSummary } from './global-ai-assistant-conversation';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import { discardStoppedTurnOutput } from './global-ai-assistant-transcript';
import type { GlobalAiConversationPayloadV1 } from './global-ai-conversation-persistence';
import { useGlobalAiConversationStateStore } from './global-ai-conversation-state-store';
import type { ReasoningTrace } from './reasoning-trace';
import {
  type GlobalAiConversationPersistenceApi,
  useGlobalAiConversationPersistence,
} from './use-global-ai-conversation-persistence';
import { useGlobalAiConversationSummarizer } from './use-global-ai-conversation-summarizer';

type GlobalAiApplyAiSearchFiltersOptions = {
  primarySelectionMergeMode?: 'merge' | 'replace';
  skipTalentDiscoveryAdaptFallback?: boolean;
};

export type GlobalAiDashboardBinding = {
  entityType: GlobalAssistantEntityType;
  dispatch: (action: TabContentAction) => void;
  getTabContent: () => TabContent;
  applyAiSearchFilters: (
    response: TalentDiscoveryAiFilterSearchResponseV2,
    options?: GlobalAiApplyAiSearchFiltersOptions
  ) => Promise<unknown>;
  getTaxonomyPayload: () => { rolesTax?: string; skillsTax?: number };
};

export type GlobalAiPendingWork =
  | {
      kind: 'customPageStream';
      prompt: string;
      model?: string;
      entityType: GlobalAssistantEntityType;
      /** Default create when omitted (backward compatible). */
      mode?: 'create' | 'update';
    }
  | {
      kind: 'singleChartApply';
      config: LLMTabContentForDispatch;
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
      entityType: GlobalAssistantEntityType;
    }
  | {
      kind: 'routeFilterApply';
      prompt: string;
      targetPath: string;
      entityType: GlobalAssistantEntityType;
      requestId: string;
    };

export type GlobalAiCustomDashAssistContext = {
  isInCustomDashEditMode: boolean;
  hasCurrentPageItems: boolean;
  hasUnsavedChanges: boolean;
  discardChanges?: () => void;
};

type GlobalAiAssistantContextValue = {
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  /** State-only close (no turn abort) — see the callback's docblock. */
  dismissDrawer: () => void;
  /** Persists draft text while the drawer is unmounted; not in context value deps. */
  draftPromptRef: React.MutableRefObject<string>;
  hasRequestedWarmUp: boolean;
  markWarmUpRequested: () => void;
  /** Latest dashboard tab binding; committed in `GlobalAIAssistantBridge` via `useLayoutEffect` (no setState). */
  bindingRef: React.MutableRefObject<GlobalAiDashboardBinding | null>;
  /** Custom Dash edit surface hints for global assistant requests (updated by bridge). */
  customDashAssistContextRef: React.MutableRefObject<GlobalAiCustomDashAssistContext>;
  pending: GlobalAiPendingWork | null;
  setPending: Dispatch<SetStateAction<GlobalAiPendingWork | null>>;
  lastUserPrompt: string | null;
  setLastUserPrompt: (value: string | null) => void;
  generationStatus: string | null;
  setGenerationStatus: (value: string | null) => void;
  streamAbortRef: React.MutableRefObject<AbortController | null>;
  /**
   * Abort controller for the active tool-loop turn. Separate from
   * `streamAbortRef` (which the bridge reuses for page-stream SSE and would
   * abort mid-turn). Set by the turn runner; aborted on drawer close so an
   * in-flight turn's SSE + poll waits actually stop.
   */
  turnAbortRef: React.MutableRefObject<AbortController | null>;
  transcript: GlobalAiTranscriptMessage[];
  appendTranscript: (message: GlobalAiTranscriptMessage) => void;
  /**
   * End a user-stopped turn: discard what it produced after `outputBaseline`
   * (the transcript length at turn start) and leave the "Stopped" marker.
   */
  markTurnStopped: (outputBaseline: number) => void;
  updateAssistantMessageText: (id: string, text: string) => void;
  updateReasoningTrace: (id: string, trace: ReasoningTrace) => void;
  /**
   * Mark a `nav_confirm`'s one-shot continuation resume as consumed. The chip
   * itself stays clickable — navigation (+ filter re-apply) can always re-run;
   * only the resumed turn is once-per-message.
   */
  markNavConfirmResolved: (id: string) => void;
  clearTranscript: () => void;
  /** DOM mount nodes for each `single_chart` transcript message id (for portaled charts). */
  chartPreviewMounts: Readonly<Record<string, HTMLElement>>;
  registerChartPreviewMount: (
    messageId: string,
    el: HTMLElement | null
  ) => void;
  /** Transcript scroll visibility for chart slots (IntersectionObserver). */
  setChartPreviewSlotViewportVisible: (
    messageId: string,
    visible: boolean
  ) => void;
  /** Message ids that should mount ChartRuntimeShell + chart portal (capped). */
  liveChartPreviewMessageIds: readonly string[];
  /** Per chart-message preview lifecycle + diagnostics (not persisted). */
  chartPreviewRuntimeByMessageId: Readonly<
    Record<string, GlobalAiChartPreviewRuntimeSnapshot>
  >;
  /** Per request submit -> backend/orchestration timeline (not persisted). */
  pipelineTimelineByRequestId: Readonly<
    Record<string, GlobalAiPipelineTimelineEvent[]>
  >;
  mergeChartPreviewRuntime: (
    messageId: string,
    patch: Partial<GlobalAiChartPreviewRuntimeSnapshot>
  ) => void;
  addChartPreviewTimelineEvent: (messageId: string, event: string) => void;
  addPipelineTimelineEvent: (requestId: string, event: string) => void;
  sessionEntityScope: GlobalAssistantEntityType | null;
  setSessionEntityScope: (value: GlobalAssistantEntityType | null) => void;
  /** Persisted conversation the transcript currently mirrors; null = unsaved new chat. */
  activeConversationId: string | null;
  setActiveConversationId: (value: string | null) => void;
  hydrateConversation: (
    payload: GlobalAiConversationPayloadV1,
    id: string
  ) => void;
  /**
   * Compact summary of older transcript turns. Set asynchronously by
   * `useGlobalAiConversationSummarizer`; `null` until the transcript
   * grows past the recency window.
   */
  conversationSummary: GlobalAiConversationSummary | null;
  setConversationSummary: Dispatch<
    SetStateAction<GlobalAiConversationSummary | null>
  >;
  conversationTrackingIdRef: React.MutableRefObject<string>;
  /** Conversation persistence (list, select/hydrate, new chat, delete). */
  persistence: GlobalAiConversationPersistenceApi;
};

const GlobalAiAssistantContext = createContext<
  GlobalAiAssistantContextValue | undefined
>(undefined);

export function GlobalAIAssistantProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const draftPromptRef = useRef('');
  const [hasRequestedWarmUp, setHasRequestedWarmUp] = useState(false);
  const bindingRef = useRef<GlobalAiDashboardBinding | null>(null);
  const customDashAssistContextRef = useRef<GlobalAiCustomDashAssistContext>({
    isInCustomDashEditMode: false,
    hasCurrentPageItems: false,
    hasUnsavedChanges: false,
    discardChanges: undefined,
  });
  const [pending, setPending] = useState<GlobalAiPendingWork | null>(null);
  const [lastUserPrompt, setLastUserPrompt] = useState<string | null>(null);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const turnAbortRef = useRef<AbortController | null>(null);
  // Fresh conversation state per provider instance (mirrors the old useState
  // lifetime; keeps StrictMode/tests/HMR semantics). Lazy initializer so it
  // runs exactly once per mount, before children render.
  useState(() => {
    useGlobalAiConversationStateStore
      .getState()
      .resetConversationState(crypto.randomUUID());
    // Same per-provider-instance lifetime for the chart-preview runtime store
    // (see its docblock's reset semantics).
    useGlobalAiChartPreviewRuntimeStore.getState().resetChartPreviewRuntime();
    return null;
  });
  // Conversation state lives in a vanilla zustand store (see the store's
  // docblock: turn-end readers need synchronous, mutually-consistent
  // snapshots that provider useState can't give). The provider subscribes
  // and re-exposes the SAME context API, so consumers are unchanged.
  const transcript = useGlobalAiConversationStateStore((s) => s.transcript);
  const sessionEntityScope = useGlobalAiConversationStateStore(
    (s) => s.sessionEntityScope
  );
  const conversationSummary = useGlobalAiConversationStateStore(
    (s) => s.conversationSummary
  );
  const setSessionEntityScope = useGlobalAiConversationStateStore(
    (s) => s.setSessionEntityScope
  );
  const setConversationSummary = useGlobalAiConversationStateStore(
    (s) => s.setConversationSummary
  );
  // Compatibility facade: consumers (engine, drawer analytics) read/write
  // `conversationTrackingIdRef.current`; back it with the store so the id
  // and the transcript always change together in one place.
  const trackingIdFacadeRef = useRef<React.MutableRefObject<string> | null>(
    null
  );
  if (trackingIdFacadeRef.current === null) {
    trackingIdFacadeRef.current = {
      get current() {
        return useGlobalAiConversationStateStore.getState().trackingId;
      },
      set current(value: string) {
        useGlobalAiConversationStateStore.getState().setTrackingId(value);
      },
    };
  }
  const conversationTrackingIdRef = trackingIdFacadeRef.current;
  const [chartPreviewMounts, setChartPreviewMounts] = useState<
    Record<string, HTMLElement>
  >({});
  const [chartPreviewViewportVisible, setChartPreviewViewportVisible] =
    useState<Record<string, boolean>>({});
  const [pipelineTimelineByRequestId, setPipelineTimelineByRequestId] =
    useState<Record<string, GlobalAiPipelineTimelineEvent[]>>({});
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);

  // Chart-preview runtime lives in a vanilla zustand store (see the store's
  // docblock: the turn runner's settle wait needs synchronous change
  // notifications that provider useState can't give). The provider
  // subscribes and re-exposes the SAME context API, so consumers are
  // unchanged.
  const chartPreviewRuntimeByMessageId = useGlobalAiChartPreviewRuntimeStore(
    (s) => s.runtimeByMessageId
  );
  const mergeChartPreviewRuntime = useGlobalAiChartPreviewRuntimeStore(
    (s) => s.mergeChartPreviewRuntime
  );
  const addChartPreviewTimelineEvent = useGlobalAiChartPreviewRuntimeStore(
    (s) => s.addChartPreviewTimelineEvent
  );

  const addPipelineTimelineEvent = useCallback(
    (requestId: string, event: string) => {
      const nextEvent: GlobalAiPipelineTimelineEvent = {
        atMs: Date.now(),
        event,
      };
      setPipelineTimelineByRequestId((prev) => {
        const timeline = [...(prev[requestId] ?? []), nextEvent];
        const trimmed =
          timeline.length > 30
            ? timeline.slice(timeline.length - 30)
            : timeline;
        return {
          ...prev,
          [requestId]: trimmed,
        };
      });
    },
    []
  );

  // Transcript mutations delegate to the conversation-state store, whose
  // append is a no-op for an id already in the transcript. Kept as stable
  // callbacks so the context API and its consumers are unchanged.
  const appendTranscript = useCallback((message: GlobalAiTranscriptMessage) => {
    useGlobalAiConversationStateStore.getState().appendMessage(message);
  }, []);

  const markTurnStopped = useCallback((outputBaseline: number) => {
    const store = useGlobalAiConversationStateStore.getState();
    store.setTranscript(
      discardStoppedTurnOutput(store.transcript, outputBaseline)
    );
  }, []);

  const updateAssistantMessageText = useCallback((id: string, text: string) => {
    useGlobalAiConversationStateStore
      .getState()
      .updateAssistantMessageText(id, text);
  }, []);

  const updateReasoningTrace = useCallback(
    (id: string, trace: ReasoningTrace) => {
      useGlobalAiConversationStateStore
        .getState()
        .updateReasoningTrace(id, trace);
    },
    []
  );

  const markNavConfirmResolved = useCallback((id: string) => {
    useGlobalAiConversationStateStore.getState().markNavConfirmResolved(id);
  }, []);

  /**
   * Shared teardown for "the conversation on screen is being replaced":
   * clears the draft and every chart/pipeline runtime map so charts remount
   * through the normal preview pipeline. Used by New Chat (clearTranscript)
   * and persisted-conversation hydration.
   */
  const resetConversationRuntime = useCallback(() => {
    draftPromptRef.current = '';
    setChartPreviewMounts({});
    setChartPreviewViewportVisible({});
    useGlobalAiChartPreviewRuntimeStore.getState().resetChartPreviewRuntime();
    setPipelineTimelineByRequestId({});
  }, []);

  const clearTranscript = useCallback(() => {
    const store = useGlobalAiConversationStateStore.getState();
    store.setTranscript([]);
    resetConversationRuntime();
    store.setSessionEntityScope(null);
    store.setConversationSummary(null);
    setActiveConversationId(null);
  }, [resetConversationRuntime]);

  /**
   * Replace the live conversation with a persisted one: abort any in-flight
   * turn, install the payload's transcript/summary/scope, and reset every
   * runtime map (chart mounts/runtimes, timelines) so charts remount through
   * the normal preview pipeline and re-fetch current data.
   */
  const hydrateConversation = useCallback(
    (payload: GlobalAiConversationPayloadV1, id: string) => {
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      turnAbortRef.current?.abort();
      turnAbortRef.current = null;
      // One store, one sync write: transcript, scope, summary and the tracking
      // id all advance in a SINGLE set(), so ANY later reader (e.g. the
      // turn-end autosave) sees a mutually-consistent snapshot and can never
      // pair the new transcript with the pre-hydration tracking id.
      useGlobalAiConversationStateStore.getState().setConversationPayload({
        transcript: payload.transcript,
        sessionEntityScope: payload.sessionEntityScope,
        conversationSummary: payload.conversationSummary,
        trackingId: id,
      });
      resetConversationRuntime();
      setLastUserPrompt(null);
      setGenerationStatus(null);
      setPending(null);
      setActiveConversationId(id);
    },
    [resetConversationRuntime]
  );

  const persistence = useGlobalAiConversationPersistence({
    activeConversationId,
    setActiveConversationId,
    hydrateConversation,
  });

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  // EXPLICIT close only (X button, ESC, FAB toggle, hidden-path effect):
  // aborts any in-flight turn by design. Surface SUPPRESSION (e.g. the host
  // hiding the drawer on the assistant homepage, where the conversation
  // renders inline) must never call this — it is render-level only, so the
  // turn keeps streaming across skin swaps.
  const closeDrawer = useCallback(() => {
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    turnAbortRef.current?.abort();
    turnAbortRef.current = null;
    setIsDrawerOpen(false);
  }, []);

  // State-only close: clears `isDrawerOpen` without aborting the turn. For
  // arriving on the homepage with the drawer state still open from an earlier
  // page — the homepage renders the conversation inline, but the stale open
  // state must not survive to pop the drawer back up on the next navigation
  // away. Any in-flight turn keeps streaming into the homepage skin.
  const dismissDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const markWarmUpRequested = useCallback(() => {
    setHasRequestedWarmUp(true);
  }, []);

  const registerChartPreviewMount = useCallback(
    (messageId: string, el: HTMLElement | null) => {
      if (el !== null) {
        mergeChartPreviewRuntime(messageId, { phase: 'mounting' });
        addChartPreviewTimelineEvent(messageId, 'mount attached');
      }
      setChartPreviewMounts((prev) => {
        if (el === null) {
          if (!(messageId in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[messageId];
          return next;
        }
        if (prev[messageId] === el) {
          return prev;
        }
        return { ...prev, [messageId]: el };
      });
    },
    [addChartPreviewTimelineEvent, mergeChartPreviewRuntime]
  );

  const setChartPreviewSlotViewportVisible = useCallback(
    (messageId: string, visible: boolean) => {
      setChartPreviewViewportVisible((prev) => {
        if (prev[messageId] === visible) {
          return prev;
        }
        return { ...prev, [messageId]: visible };
      });
      if (visible) {
        addChartPreviewTimelineEvent(messageId, 'slot became visible');
      }
    },
    [addChartPreviewTimelineEvent]
  );

  const singleChartMessageIdsOrdered = useMemo(() => {
    return (
      transcript
        .filter(
          (
            m
          ): m is Extract<
            GlobalAiTranscriptMessage,
            { kind: 'single_chart' }
          > => m.role === 'assistant' && m.kind === 'single_chart'
        )
        // answerAsText single_chart messages are offer-wiring metadata only:
        // their digest comes from the headless service, so they must never
        // occupy live chart-preview slots.
        .filter((m) => m.answerAsText !== true)
        .map((m) => m.id)
    );
  }, [transcript]);

  const viewportVisibleIds = useMemo(() => {
    return new Set(
      Object.entries(chartPreviewViewportVisible)
        .filter(([, v]) => v)
        .map(([k]) => k)
    );
  }, [chartPreviewViewportVisible]);

  const liveChartPreviewMessageIds = useMemo(
    () =>
      pickLiveChartPreviewMessageIds(
        singleChartMessageIdsOrdered,
        viewportVisibleIds
      ),
    [singleChartMessageIdsOrdered, viewportVisibleIds]
  );

  useGlobalAiConversationSummarizer({
    transcript,
    summary: conversationSummary,
    setSummary: setConversationSummary,
  });

  const value = useMemo(
    () => ({
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      dismissDrawer,
      draftPromptRef,
      hasRequestedWarmUp,
      markWarmUpRequested,
      bindingRef,
      customDashAssistContextRef,
      pending,
      setPending,
      lastUserPrompt,
      setLastUserPrompt,
      generationStatus,
      setGenerationStatus,
      streamAbortRef,
      turnAbortRef,
      transcript,
      appendTranscript,
      markTurnStopped,
      updateAssistantMessageText,
      updateReasoningTrace,
      markNavConfirmResolved,
      clearTranscript,
      chartPreviewMounts,
      registerChartPreviewMount,
      setChartPreviewSlotViewportVisible,
      liveChartPreviewMessageIds,
      chartPreviewRuntimeByMessageId,
      pipelineTimelineByRequestId,
      mergeChartPreviewRuntime,
      addChartPreviewTimelineEvent,
      addPipelineTimelineEvent,
      sessionEntityScope,
      setSessionEntityScope,
      activeConversationId,
      setActiveConversationId,
      hydrateConversation,
      conversationSummary,
      setConversationSummary,
      conversationTrackingIdRef,
      persistence,
    }),
    [
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      dismissDrawer,
      hasRequestedWarmUp,
      markWarmUpRequested,
      pending,
      lastUserPrompt,
      generationStatus,
      transcript,
      appendTranscript,
      markTurnStopped,
      updateAssistantMessageText,
      updateReasoningTrace,
      markNavConfirmResolved,
      clearTranscript,
      chartPreviewMounts,
      registerChartPreviewMount,
      setChartPreviewSlotViewportVisible,
      liveChartPreviewMessageIds,
      chartPreviewRuntimeByMessageId,
      pipelineTimelineByRequestId,
      mergeChartPreviewRuntime,
      addChartPreviewTimelineEvent,
      addPipelineTimelineEvent,
      sessionEntityScope,
      activeConversationId,
      hydrateConversation,
      conversationSummary,
      // Stable store-backed values (zustand action identities never change;
      // the facade is created once per provider) — listed because they are
      // no longer useState/useRef results, which is what biome's stability
      // exemption keys on.
      setSessionEntityScope,
      setConversationSummary,
      conversationTrackingIdRef,
      persistence,
    ]
  );

  return (
    <GlobalAiAssistantContext.Provider value={value}>
      {children}
    </GlobalAiAssistantContext.Provider>
  );
}

export function useGlobalAIAssistant(): GlobalAiAssistantContextValue {
  const ctx = useContext(GlobalAiAssistantContext);
  if (!ctx) {
    throw new Error(
      'useGlobalAIAssistant must be used within GlobalAIAssistantProvider'
    );
  }
  return ctx;
}

export function useGlobalAIAssistantOptional():
  | GlobalAiAssistantContextValue
  | undefined {
  return useContext(GlobalAiAssistantContext);
}
